import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Eye, LoaderCircle, RefreshCw, Sparkles, XCircle } from 'lucide-react';
import EmptyState from '../shared/EmptyState';
import ErrorState from '../shared/ErrorState';
import LoadingSkeleton from '../shared/LoadingSkeleton';
import './StoreIntelligencePanel.css';

const DEFAULT_INTELLIGENCE = {
  summary: null,
  suggestions: [],
  moduleSummaries: [],
  isEmpty: true,
  isLoading: false,
  isRefreshing: false,
  isMutating: false,
  isGeneratingSummary: false,
  canGenerateSummary: true,
  generationRetryAtMs: null,
  isError: false,
  error: null,
  refetchLatest: null,
  generateSummary: null,
  markSuggestionViewed: null,
  dismissSuggestion: null,
  generateOfferDraftFromSuggestion: null
};

const formatCooldown = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${Math.max(1, minutes)}m`;
};

// The manual-generation cooldown is week-aligned: it unlocks at the start of the next
// ISO week (Monday), so the day it becomes available reads far clearer than a raw
// countdown ("Available Monday" vs "Available in 1d 23h"). Only when it unlocks later
// the same day do we fall back to a short duration.
const formatCooldownTarget = (retryAtMs, nowMs) => {
  const remainingMs = retryAtMs - nowMs;
  const target = new Date(retryAtMs);
  const durationFallback = () => {
    const duration = `in ${formatCooldown(remainingMs)}`;
    return { badge: duration, full: duration };
  };
  if (Number.isNaN(target.getTime())) {
    return durationFallback();
  }
  const startOfToday = new Date(nowMs);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTarget = new Date(target);
  startOfTarget.setHours(0, 0, 0, 0);
  const dayDiff = Math.round((startOfTarget.getTime() - startOfToday.getTime()) / 86400000);
  if (dayDiff <= 0) {
    return durationFallback();
  }
  const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(target);
  const dayMonth = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' }).format(target);
  const prefix = dayDiff === 1 ? 'tomorrow, ' : '';
  return { badge: weekday, full: `${prefix}${weekday} ${dayMonth}` };
};

const GENERATION_STEPS = [
  'Reading store performance signals',
  'Comparing recent customer behavior',
  'Summarizing risks and strengths',
  'Preparing practical strategy directions'
];

const MODULE_LABELS = {
  ABANDONMENT: 'Abandonment',
  APPOINTMENTS: 'Appointments',
  BOTTLENECK: 'Bottlenecks',
  CUSTOMERS: 'Customers',
  OFFERS: 'Offers',
  PEAK: 'Peak',
  QUEUE: 'Queue',
  REVIEWS: 'Reviews',
  STAFFING: 'Staffing',
  WHATIF: 'What-if'
};

const PRIORITY_LABELS = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low'
};

const STATUS_LABELS = {
  OPEN: 'Open',
  VIEWED: 'Viewed',
  DISMISSED: 'Dismissed',
  USED_FOR_OFFER: 'Used for offer',
  GENERATING_OFFER: 'Generating offer…'
};

const StoreIntelligencePanel = ({ intelligence = DEFAULT_INTELLIGENCE }) => {
  const {
    summary = {},
    suggestions = [],
    moduleSummaries = [],
    isEmpty = true,
    isLoading = false,
    isRefreshing = false,
    isMutating = false,
    isGeneratingSummary = false,
    canGenerateSummary = true,
    generationRetryAtMs = null,
    isError = false,
    error = null,
    refetchLatest,
    generateSummary,
    markSuggestionViewed,
    dismissSuggestion,
    generateOfferDraftFromSuggestion
  } = intelligence || DEFAULT_INTELLIGENCE;

  const [draftActions, setDraftActions] = useState({});
  // Live countdown for the manual-generation cooldown so the button can show when
  // it unlocks, just like the AI-offer generation quota.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!generationRetryAtMs || generationRetryAtMs <= Date.now()) {
      return undefined;
    }
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(intervalId);
  }, [generationRetryAtMs]);
  const onGenerationCooldown = !canGenerateSummary && Boolean(generationRetryAtMs) && generationRetryAtMs > nowMs;
  const cooldownTarget = onGenerationCooldown ? formatCooldownTarget(generationRetryAtMs, nowMs) : null;
  const generateDisabled = isGeneratingSummary || isMutating || isRefreshing || onGenerationCooldown;
  const generateLabel = isGeneratingSummary
    ? 'Generating summary'
    : onGenerationCooldown
      ? `Available ${cooldownTarget.badge}`
      : 'Generate summary';
  const generateSummaryButton = typeof generateSummary === 'function' ? (
    <button
      type="button"
      className="btn primary small store-intelligence-generate-btn"
      onClick={generateSummary}
      disabled={generateDisabled}
      title={onGenerationCooldown ? `Next summary available ${cooldownTarget.full}` : undefined}
    >
      <Sparkles size={14} aria-hidden="true" />
      {generateLabel}
    </button>
  ) : null;
  const maturityLabel = summary?.maturityLabel || formatLabel(summary?.maturityStage) || 'Not classified';
  const visibleSuggestions = suggestions
    .filter((suggestion) => suggestion.status !== 'DISMISSED')
    .slice(0, 5);

  useEffect(() => {
    if (typeof document === 'undefined' || !isGeneratingSummary) {
      return undefined;
    }
    document.body.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isGeneratingSummary]);

  const handleCreateDraft = async (suggestion) => {
    if (typeof generateOfferDraftFromSuggestion !== 'function' || !suggestion?.id) {
      return;
    }
    setDraftActions((current) => ({
      ...current,
      [suggestion.id]: { loading: true, draft: null, error: null }
    }));
    try {
      const draft = await generateOfferDraftFromSuggestion(suggestion.id);
      setDraftActions((current) => ({
        ...current,
        [suggestion.id]: { loading: false, draft, error: null }
      }));
    } catch (error) {
      setDraftActions((current) => ({
        ...current,
        [suggestion.id]: {
          loading: false,
          draft: null,
          error: error?.data?.message || error?.message || 'Unable to create offer draft.'
        }
      }));
    }
  };

  const generationModal = isGeneratingSummary && typeof document !== 'undefined'
    ? createPortal(
      <div className="zone-modal-overlay store-intelligence-generation-overlay" role="presentation">
        <div
          className="zone-modal store-intelligence-generation-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Generating Store Health summary"
        >
          <div className="store-intelligence-generation-orb" aria-hidden="true">
            <LoaderCircle size={34} />
          </div>
          <div className="store-intelligence-generation-copy">
            <p className="store-intelligence-eyebrow">Store Health</p>
            <h3>Generating health summary</h3>
            <p>Reviewing store signals and preparing a weekly AI readout.</p>
          </div>
          <div className="store-intelligence-generation-steps" aria-label="Generation progress">
            {GENERATION_STEPS.map((step, index) => (
              <div className="store-intelligence-generation-step" key={step}>
                <span>{index + 1}</span>
                <p>{step}</p>
              </div>
            ))}
          </div>
          <div className="store-intelligence-generation-bar" aria-hidden="true">
            <span />
          </div>
        </div>
      </div>,
      document.body
    )
    : null;

  return (
    <section className="store-intelligence-panel" aria-labelledby="store-intelligence-title">
      {generationModal}
      <header className="store-intelligence-header">
        <div className="store-intelligence-title-wrap">
          <div className="store-intelligence-icon" aria-hidden="true">
            <Sparkles size={16} />
          </div>
          <div>
            <p className="store-intelligence-eyebrow">Weekly intelligence</p>
            <h4 id="store-intelligence-title">Store summary</h4>
          </div>
        </div>
        <div className="store-intelligence-header-actions">
          <span className="store-intelligence-maturity">{maturityLabel}</span>
          {!isEmpty ? generateSummaryButton : null}
          {typeof refetchLatest === 'function' ? (
            <button
              type="button"
              className="store-intelligence-icon-btn"
              onClick={refetchLatest}
              disabled={isLoading || isRefreshing}
              aria-label="Refresh store intelligence"
              title="Refresh"
            >
              <RefreshCw size={15} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </header>

      {isLoading ? (
        <LoadingSkeleton />
      ) : null}

      {!isLoading && isError ? (
        <ErrorState
          message={error?.data?.message || error?.message || 'Unable to load Store Intelligence.'}
          onRetry={refetchLatest}
        />
      ) : null}

      {!isLoading && !isError && isEmpty ? (
        <div className="store-intelligence-empty">
          <EmptyState message="No weekly Store Intelligence summary has been generated yet." />
          {onGenerationCooldown ? (
            <p className="store-intelligence-cooldown-note">{`Next summary available ${cooldownTarget.full}.`}</p>
          ) : null}
          {generateSummaryButton}
        </div>
      ) : null}

      {!isLoading && !isError && !isEmpty ? (
        <div className="store-intelligence-body">
          <div className="store-intelligence-summary-grid">
            <article className="store-intelligence-summary-main">
              <div className="store-intelligence-period">
                {formatPeriod(summary?.periodStart, summary?.periodEnd)}
              </div>
              <h5>{summary?.headline || 'Latest store readout'}</h5>
              <p>{summary?.compoundSummary || summary?.executiveSummary || 'Summary is being prepared.'}</p>
              {summary?.whatChanged ? (
                <div className="store-intelligence-change">
                  <span>Changed</span>
                  <p>{summary.whatChanged}</p>
                </div>
              ) : null}
            </article>

            <aside className="store-intelligence-focus">
              <SignalList title="Strengths" items={summary?.topStrengths} />
              <SignalList title="Risks" items={summary?.topRisks} />
              <SignalList title="Focus" items={summary?.recommendedFocus} />
            </aside>
          </div>

          {moduleSummaries.length > 0 ? (
            <div className="store-intelligence-module-grid" aria-label="Module summaries">
              {moduleSummaries.slice(0, 6).map((moduleSummary) => (
                <article className="store-intelligence-module" key={moduleSummary.id || moduleSummary.moduleType}>
                  <div className="store-intelligence-module-head">
                    <span>{formatModuleLabel(moduleSummary.moduleType)}</span>
                    {moduleSummary.confidence != null ? (
                      <strong>{Math.round(Number(moduleSummary.confidence) * 100)}%</strong>
                    ) : null}
                  </div>
                  <h6>{moduleSummary.headline || 'Module summary'}</h6>
                  <p>{moduleSummary.summary || 'No module summary available.'}</p>
                </article>
              ))}
            </div>
          ) : null}

          <section className="store-intelligence-suggestions" aria-label="Strategy suggestions">
            <div className="store-intelligence-section-head">
              <h5>Suggested direction</h5>
              <span>{visibleSuggestions.length} shown</span>
            </div>
            {visibleSuggestions.length > 0 ? (
              <div className="store-intelligence-suggestion-list">
                {visibleSuggestions.map((suggestion) => (
                  <article className="store-intelligence-suggestion" key={suggestion.id}>
                    <div className="store-intelligence-suggestion-top">
                      <div>
                        <div className="store-intelligence-suggestion-meta">
                          <span>{PRIORITY_LABELS[suggestion.priority] || formatLabel(suggestion.priority) || 'Normal'}</span>
                          <span>{STATUS_LABELS[suggestion.status] || formatLabel(suggestion.status)}</span>
                        </div>
                        <h6>{suggestion.title || 'Suggestion'}</h6>
                      </div>
                      <div className="store-intelligence-suggestion-actions">
                        {suggestion.status === 'OPEN' && typeof markSuggestionViewed === 'function' ? (
                          <button
                            type="button"
                            className="store-intelligence-icon-btn"
                            onClick={() => markSuggestionViewed(suggestion.id)}
                            disabled={isMutating}
                            aria-label={`Mark ${suggestion.title || 'suggestion'} as viewed`}
                            title="Mark viewed"
                          >
                            <Eye size={15} aria-hidden="true" />
                          </button>
                        ) : null}
                        {!['DISMISSED', 'USED_FOR_OFFER'].includes(suggestion.status)
                          && typeof dismissSuggestion === 'function' ? (
                            <button
                              type="button"
                              className="store-intelligence-icon-btn danger"
                              onClick={() => dismissSuggestion(suggestion.id)}
                              disabled={isMutating}
                              aria-label={`Dismiss ${suggestion.title || 'suggestion'}`}
                              title="Dismiss"
                            >
                              <XCircle size={15} aria-hidden="true" />
                            </button>
                          ) : null}
                      </div>
                    </div>
                    <p>{suggestion.description}</p>
                    {suggestion.suggestedAction ? (
                      <div className="store-intelligence-action-text">
                        <span>Direction</span>
                        <p>{suggestion.suggestedAction}</p>
                      </div>
                    ) : null}
                    {suggestion.sourceModules?.length ? (
                      <div className="store-intelligence-source-list">
                        {suggestion.sourceModules.slice(0, 4).map((source) => (
                          <span key={source}>{formatModuleLabel(source)}</span>
                        ))}
                      </div>
                    ) : null}
                    {suggestion.canGenerateOffer
                      || suggestion.isUsedForOffer
                      || suggestion.status === 'GENERATING_OFFER'
                      || draftActions[suggestion.id]?.draft ? (
                        <div className="store-intelligence-draft-action">
                          {(suggestion.canGenerateOffer || suggestion.status === 'GENERATING_OFFER')
                            && typeof generateOfferDraftFromSuggestion === 'function' ? (
                              <button
                                type="button"
                                className="btn ghost small store-intelligence-draft-btn"
                                onClick={() => handleCreateDraft(suggestion)}
                                disabled={
                                  isMutating
                                  || draftActions[suggestion.id]?.loading
                                  || suggestion.status === 'GENERATING_OFFER'
                                }
                              >
                                <Sparkles size={14} aria-hidden="true" />
                                {draftActions[suggestion.id]?.loading || suggestion.status === 'GENERATING_OFFER'
                                  ? 'Generating draft…'
                                  : 'Create draft'}
                              </button>
                            ) : null}
                          {suggestion.isUsedForOffer
                          || suggestion.generatedOfferRecommendationId
                          || draftActions[suggestion.id]?.draft ? (
                            <span className="store-intelligence-draft-note">
                              {draftActions[suggestion.id]?.draft?.draft?.title
                                ? `Draft created: ${draftActions[suggestion.id].draft.draft.title}`
                                : `Draft created${suggestion.generatedOfferRecommendationId
                                  ? ` #${suggestion.generatedOfferRecommendationId}`
                                  : ''}`}
                            </span>
                          ) : null}
                          {draftActions[suggestion.id]?.error ? (
                            <span className="store-intelligence-draft-note error" role="alert">
                              {draftActions[suggestion.id].error}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState message="No active Store Intelligence suggestions for this summary." />
            )}
          </section>
        </div>
      ) : null}
    </section>
  );
};

const SignalList = ({ title, items = [] }) => {
  const visibleItems = Array.isArray(items) ? items.slice(0, 3) : [];
  if (visibleItems.length === 0) {
    return null;
  }
  return (
    <div className="store-intelligence-signal-list">
      <span>{title}</span>
      <ul>
        {visibleItems.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

const formatModuleLabel = (value) => MODULE_LABELS[String(value || '').toUpperCase()] || formatLabel(value) || 'Module';

const formatLabel = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '';
  }
  return normalized
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatPeriod = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return 'Latest weekly read';
  }
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

const formatDate = (value) => {
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' }).format(parsed);
};

export default StoreIntelligencePanel;
